import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { business, navLinks, scrollToId, waLink } from "@/config/business";

export default function Header({ onBook }) {
  const [open, setOpen] = useState(false);
  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header className="sticky top-0 z-50 bg-cream/90 backdrop-blur-md border-b border-primary/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 h-[72px] flex items-center justify-between">
        <button
          data-testid="logo-button"
          onClick={() => go("home")}
          className="text-left leading-none"
          aria-label="Lavniya Beauty Salon — go to top"
        >
          <span className="font-display text-2xl md:text-3xl font-semibold text-primary tracking-tight">
            Lavniya
          </span>
          <span className="block text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-0.5">
            Beauty Salon
          </span>
        </button>

        <nav aria-label="Primary" className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}-link`}
              onClick={() => go(l.id)}
              className="text-sm font-medium tracking-wide text-ink/80 hover:text-primary transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            data-testid="header-whatsapp-button"
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-wa text-wa px-5 py-2.5 text-sm font-semibold tracking-wide hover:bg-wa hover:text-white transition-colors"
          >
            <MessageCircle size={16} /> WhatsApp
          </a>
          <button
            data-testid="header-book-button"
            onClick={onBook}
            className="bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold tracking-wide hover:bg-deep transition-colors"
          >
            Book Appointment
          </button>
        </div>

        <button
          data-testid="mobile-menu-button"
          className="lg:hidden p-3 -mr-3 text-primary"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            aria-label="Mobile"
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-cream border-t border-primary/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {navLinks.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}-link`}
                  onClick={() => go(l.id)}
                  className="text-left font-display text-2xl text-ink py-3 border-b border-primary/10 hover:text-primary transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-book-button"
                onClick={() => {
                  setOpen(false);
                  onBook();
                }}
                className="mt-5 bg-primary text-primary-foreground px-6 py-4 text-sm font-semibold tracking-widest uppercase hover:bg-deep transition-colors"
              >
                Book Appointment
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
